<template>
  <div class="cashback-pagina">
    <Loading :isActive="carregando && !jaCarregou" />

    <div class="cashback-aviso">
      <fa-icon icon="info-circle" class="cashback-aviso__icone" />
      <span>
        Clientes bloqueados não ganham nem usam cashback e não veem nada do programa nos canais de venda. O saldo fica guardado com o vencimento
        normal: ao desbloquear, o que ainda não venceu volta a poder ser usado. Para tirar o saldo, abra o cliente na aba Carteiras e use Remover saldo.
      </span>
    </div>

    <div class="cashback-filtros">
      <div class="cashback-filtros__linha">
        <div class="cashback-busca">
          <input
            class="form-control"
            v-model="termo"
            placeholder="Buscar cliente bloqueado por nome, código ou CNPJ..."
            aria-label="Buscar cliente bloqueado"
            @input="carregarComDebounce()"
          />
        </div>
        <div class="cashback-filtros__acoes">
          <CButton color="danger" class="cashback-botao-texto" @click="abrirBloqueio">
            <fa-icon icon="ban" /> Bloquear cliente
          </CButton>
        </div>
      </div>
    </div>

    <div class="cashback-tabela-caixa">
      <table class="cashback-tabela">
        <thead>
          <tr>
            <th>Cliente</th>
            <th style="width: 130px">Saldo</th>
            <th>Motivo</th>
            <th style="width: 200px">Bloqueado por</th>
            <th style="width: 130px"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="erro">
            <td colspan="5" class="cashback-tabela__vazio">
              <div class="cashback-tabela__erro">Não foi possível carregar os bloqueios: {{ erro }}</div>
              <CButton color="primary" variant="outline" size="sm" @click="carregar(pagina)">Tentar novamente</CButton>
            </td>
          </tr>
          <tr v-else-if="!itens.length">
            <td colspan="5" class="cashback-tabela__vazio">Nenhum cliente bloqueado</td>
          </tr>
          <tr v-for="item in itens" :key="item.clienteId">
            <td>
              <div class="cashback-bloqueios__nome">{{ item.nome || `Cliente ${item.clienteId}` }}</div>
              <div class="cashback-tabela__secundario">
                Código {{ item.clienteId }}<template v-if="item.cnpj"> · {{ formatDocumento(item.cnpj) }}</template>
              </div>
            </td>
            <td :class="classeSaldo(item.saldo)">{{ formatMoney(item.saldo) }}</td>
            <td class="cashback-tabela__secundario">{{ item.motivo }}</td>
            <td>
              {{ item.colaborador || "—" }}
              <div class="cashback-tabela__secundario">{{ item.bloqueadoEm | datetime }}</div>
            </td>
            <td>
              <CButton color="success" variant="outline" size="sm" :disabled="desbloqueando === item.clienteId" @click="desbloquear(item)">
                <fa-icon icon="unlock" /> Desbloquear
              </CButton>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="cashback-paginacao" v-if="paginator.last_page > 1">
      <pagination :data="paginator" :limit="5" @pagination-change-page="carregar" />
    </div>

    <BloquearClienteModal :show.sync="bloqueando" @bloqueado="carregar(1)" />
  </div>
</template>

<script>
import _debounce from "lodash/debounce";
import Loading from "../../components/Loading";
import { del, get } from "@/helpers/apiRequest";
import BloquearClienteModal from "./components/BloquearClienteModal.vue";
import { DEBOUNCE_BUSCA_MS, classeSaldo, formatDocumento, formatMoney, mensagemErroApi } from "./helpers";

const POR_PAGINA = 20;

export default {
  name: "cashback-bloqueios",
  components: { Loading, BloquearClienteModal },

  data() {
    return {
      termo: "",
      itens: [],
      paginator: {},
      pagina: 1,
      carregando: false,
      jaCarregou: false,
      erro: null,
      desbloqueando: null,
      bloqueando: false,
    };
  },

  mounted() {
    this.carregar();
  },

  methods: {
    classeSaldo,
    formatDocumento,
    formatMoney,

    carregar(pagina = 1) {
      const seq = (this._loadSeq = (this._loadSeq || 0) + 1);

      this.carregando = true;
      this.erro = null;

      return get(`/admin/cashback/bloqueios?page=${pagina}&take=${POR_PAGINA}&termo=${encodeURIComponent(this.termo)}`)
        .then((json) => {
          if (seq !== this._loadSeq) return;

          this.itens = json.itens || [];
          this.pagina = json.pagina || pagina;
          this.paginator = {
            current_page: this.pagina,
            last_page: Math.max(1, Math.ceil((json.total || 0) / POR_PAGINA)),
            total: json.total || 0,
            per_page: POR_PAGINA,
          };
        })
        .catch((err) => {
          if (seq !== this._loadSeq) return;

          this.itens = [];
          this.erro = mensagemErroApi(err);
        })
        .finally(() => {
          if (seq !== this._loadSeq) return;

          this.carregando = false;
          this.jaCarregou = true;
        });
    },

    carregarComDebounce: _debounce(function () {
      this.carregar();
    }, DEBOUNCE_BUSCA_MS),

    desbloquear(item) {
      return this.$swal({
        title: "Desbloquear o cliente?",
        text: "Os próximos pedidos voltam a gerar cashback conforme as regras e o saldo que ainda não venceu volta a poder ser usado. Pedidos feitos durante o bloqueio não são recalculados.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Desbloquear",
        cancelButtonText: "Cancelar",
      }).then((res) => {
        if (!res.value) return;

        this.desbloqueando = item.clienteId;
        return del(`/admin/cashback/bloqueios/${encodeURIComponent(item.clienteId)}`)
          .then(() => this.carregar(this.itens.length === 1 && this.pagina > 1 ? this.pagina - 1 : this.pagina))
          .catch((err) => this.$swal({ title: "Não foi possível desbloquear", text: mensagemErroApi(err), icon: "error" }))
          .finally(() => {
            this.desbloqueando = null;
          });
      });
    },

    abrirBloqueio() {
      this.bloqueando = true;
    },
  },
};
</script>

<style scoped src="./cashback.css"></style>

<style scoped>
.cashback-bloqueios__nome {
  font-weight: 600;
  color: #101828;
}
</style>
